import { z } from 'zod'

/**
 * A venue's marketing page, as the pricing site reads it.
 *
 * This is the contract with an app we do not deploy, so it is the whole of what
 * that app may rely on. See `docs/venue-marketing.md`.
 *
 * **Every price-like field is a string**, never a number: `"3,500"`, `"$3,500"`,
 * `"From $2,500"`, `"Inquire"`. Most are rendered verbatim and never parsed, so
 * a number type here would force a formatting decision this side cannot make.
 * Three are the exception: the Snapshot, Premium and Micro prices sit beside a
 * dollar sign the site prints itself, so a save holds them to a plain number
 * (`displayPriceNumber`).
 *
 * **Every image field is a string**: an absolute http(s) URL, or empty for no
 * image, and the page falls back to text rather than to a gap. The site puts
 * them inside CSS `url('…')`, so a quote is refused wherever it appears.
 *
 * **An absent optional section is what turns it off.** There is no `enabled`
 * flag anywhere: a page has a `buyout` section or it does not.
 */

/** Text that appears on the page. Bounded, because a page is not a database. */
const text = z.string().max(4000)
const shortText = z.string().max(300)
/** A price, a label, a duration — always a display string, never a number. */
const priceText = z.string().max(120)

/** "3500", "3,500" or "3,500.00": what reads right after the dollar sign the site prints. */
const PRICE_NUMBER = /^(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{2})?$/

/**
 * A price the pricing site prints after its own superscript `$`.
 *
 * Read as stored, trimmed: refusing a stored "From $2,500" here would take the
 * venue's whole page down over one card. A save holds it to a number instead —
 * see `saveVenueMarketingContentSchema` — so a new one cannot become "$From $2,500".
 */
const dollarPrice = priceText.trim()

const PRICE_NUMBER_MESSAGE = 'Enter a number such as 3,500. The page adds the dollar sign.'

/** The save-side rule for a `dollarPrice`, which the editor reports on the field. */
export const displayPriceNumber = priceText.trim().regex(PRICE_NUMBER, PRICE_NUMBER_MESSAGE)

/** The same, where a card may leave its price empty and the site then prints none. */
const displayPriceNumberOrEmpty = priceText
  .trim()
  .refine((price) => price === '' || PRICE_NUMBER.test(price), PRICE_NUMBER_MESSAGE)

/**
 * An image: an absolute http(s) URL, or empty for "none". Checked against every
 * stored page on 2026-10-29 before it was enforced on reading too.
 */
const imageRef = z
  .string()
  .max(1000)
  .refine((ref) => ref === '' || (/^https?:\/\//i.test(ref) && !/['"\\\s]/.test(ref)), {
    message: 'Use the image’s http(s) address, with no quotes or spaces, or leave it empty.',
  })

/**
 * A link the pricing site assigns to `href` with no further check.
 *
 * Empty means no link, or a button that is present but goes nowhere
 * (`bookingPrivate: ''`). A leading `#` is a same-page anchor — one live page's
 * wedding button is `#tour`. Anything else has to be an http(s) address.
 * `javascript:`, `data:`, and a protocol-relative `//…` are refused: the site
 * would run them, or leave the page, from a public link.
 */
function isPageLink(value: string): boolean {
  const link = value.trim()
  if (link === '') return true
  if (link.startsWith('#')) return true
  return /^https?:\/\//i.test(link)
}

const pageLink = z.string().max(1000).refine(isPageLink, 'Use a web address (https://…) or a link on this page (#…).')

/**
 * The heading, in at most one of three styles.
 *
 * A venue uses a wordmark image, or a three-part sentence with the middle part
 * emphasized, or three stacked lines. **Two of them set is a page with two
 * headings**, so that is refused.
 *
 * *None* set is not. The brief said "exactly one of these three is expected",
 * and the ten live documents disagree: five set one and five set none, carrying
 * `lettering: ""` and nothing else. That is the documented behavior of an empty
 * image field — "empty string or omitted = no image", with a graceful text-only
 * fallback — so a hero with no wordmark renders its subheading and is fine.
 * Requiring one refused half the real content on the first import, which is the
 * argument for reading the data before writing the rule.
 */
const heroSchema = z
  .object({
    image: imageRef,
    sub: text,
    lettering: imageRef.optional(),
    headPre: shortText.optional(),
    headEm: shortText.optional(),
    headPost: shortText.optional(),
    line1: shortText.optional(),
    line2: shortText.optional(),
    line3: shortText.optional(),
  })
  .refine(
    (hero) => {
      // Emptiness counts the same way for all three. A JSONBin export carries
      // every key the template ever had, most of them empty strings — so
      // "present" would read a document with `lettering` set and `headPre: ""`
      // as two headings and refuse it, and one with only `headPre: ""` as one
      // heading while it renders none.
      const set = (...values: (string | undefined)[]): boolean =>
        values.some((value) => value !== undefined && value !== '')
      const styles = [
        set(hero.lettering),
        set(hero.headPre, hero.headEm, hero.headPost),
        set(hero.line1, hero.line2, hero.line3),
      ].filter(Boolean)
      return styles.length <= 1
    },
    {
      message:
        'The hero has two headings. Use one of: a lettering image, the ' +
        'headPre/headEm/headPost wording, or the line1/line2/line3 wording.',
    },
  )

const introSchema = z.object({
  heading: shortText,
  body: text,
  /** The small pill above the fold, e.g. "★ 2026–27 Pricing · $350 Admin Fee". */
  tag: shortText,
})

/** The "meet your planner" block. `initial` is the monogram shown with no photo. */
const guideSchema = z.object({
  name: shortText,
  role: shortText,
  bio: text,
  sign: shortText,
  btn: shortText.optional(),
  photo: imageRef.optional(),
  /**
   * The monogram shown when there is no photograph.
   *
   * The brief calls it a "single-letter fallback" and the live documents run to
   * three — "TLA" for The Lodge Alabama. Four characters leaves room without
   * admitting a word.
   */
  initial: z.string().max(4).optional(),
})

const snapshotCardSchema = z.object({
  season: shortText,
  months: shortText,
  startlabel: shortText,
  /** Digits only — the template adds the currency symbol itself. */
  price: dollarPrice,
})

const snapshotSchema = z.object({
  heading: shortText,
  body: text,
  /** The closing sentence summarizing the whole range. */
  range: text,
  cards: z.array(snapshotCardSchema).max(12),
})

const includedSchema = z.object({
  heading: shortText,
  items: z.array(shortText).max(60),
  photo: imageRef.optional(),
  caption: shortText.optional(),
})

/** One of the two price tables. `rows` is positional — see `pricingSchema`. */
const priceTableSchema = z.object({
  label: shortText,
  months: shortText,
  rows: z.array(z.array(priceText).max(12)).max(60),
})

/**
 * The price tables, and the two rules the brief states and enforces nowhere.
 *
 * **Columns come as one set or two, never neither and never half a pair.** Most
 * venues give `columns` and both tables share them; a few have genuinely
 * different layouts and give `columnsHigh` and `columnsLow`.
 *
 * **Every row is exactly as wide as its columns.** The cells are positional —
 * there are no field names — so a row one cell short does not render a blank
 * cell, it shifts every price into the wrong column. That is a page quietly
 * showing Saturday's price under Monday, which nobody looking at the form would
 * catch and every couple reading the page would.
 */
const pricingSchema = z
  .object({
    heading: shortText,
    body: text,
    foot: text,
    high: priceTableSchema,
    low: priceTableSchema,
    columns: z.array(shortText).max(12).optional(),
    columnsHigh: z.array(shortText).max(12).optional(),
    columnsLow: z.array(shortText).max(12).optional(),
  })
  .superRefine((pricing, ctx) => {
    const shared = pricing.columns !== undefined
    const split = pricing.columnsHigh !== undefined && pricing.columnsLow !== undefined
    const halfSplit =
      (pricing.columnsHigh === undefined) !== (pricing.columnsLow === undefined)

    if (halfSplit) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Give column headers for both tables or for neither: columnsHigh without columnsLow ' +
          '(or the reverse) leaves one table with no headings.',
      })
      return
    }
    if (!shared && !split) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Give the tables column headers: either `columns` for both, or `columnsHigh` and ' +
          '`columnsLow` separately.',
      })
      return
    }

    const highColumns = pricing.columnsHigh ?? pricing.columns ?? []
    const lowColumns = pricing.columnsLow ?? pricing.columns ?? []
    for (const [table, columns, path] of [
      [pricing.high, highColumns, 'high'],
      [pricing.low, lowColumns, 'low'],
    ] as const) {
      table.rows.forEach((row, index) => {
        if (row.length !== columns.length) {
          ctx.addIssue({
            code: 'custom',
            path: [path, 'rows', index],
            message:
              `Row ${String(index + 1)} has ${String(row.length)} cells but there are ` +
              `${String(columns.length)} columns. Every cell is positional, so a row of the ` +
              `wrong width puts prices under the wrong headings.`,
          })
        }
      })
    }
  })

const addonsSchema = z.object({
  heading: shortText,
  /** May be empty — several venues run this section with no preamble. */
  body: text,
  rows: z
    .array(z.object({ name: shortText, sub: shortText.optional(), price: priceText }))
    .max(60),
})

/** "What people typically spend" — tiers, not a price list. */
const spendSchema = z.object({
  heading: shortText,
  body: text,
  note: text,
  cards: z
    .array(
      z.object({
        /** Tier name, guest count, price range, description. Terse keys are the
         * brief's, kept because the consuming app reads them by these names. */
        t: shortText,
        g: shortText,
        r: priceText,
        d: text,
      }),
    )
    .max(12),
})

const microCardSchema = z.object({
  title: shortText.optional(),
  /** Optional, and empty shows no price: the site prints the `$` only when there is one. */
  price: dollarPrice.optional(),
  meta: shortText.optional(),
  desc: text.optional(),
  image: imageRef.optional(),
})

const microSchema = z.object({
  heading: shortText,
  body: text,
  /**
   * A section image, which the brief does not list.
   *
   * Launch Pointe carries one — a real Cloudinary URL — and the brief says it
   * was "derived field-by-field from the real content of all 10 existing sites",
   * so this is a field that derivation missed rather than one somebody invented.
   * Kept because dropping it would quietly remove an image from a live page, and
   * an optional field nothing reads costs nothing.
   */
  image: imageRef.optional(),
  cards: z.array(microCardSchema).max(20),
  tasting: z.object({
    heading: shortText,
    body: text,
    image: imageRef.optional(),
    /** A small label/value grid: `{ l: "Capacity", v: "Flexible" }`. */
    meta: z.array(z.object({ l: shortText, v: shortText })).max(12),
  }),
})

/**
 * The fine print.
 *
 * `items` is `{ l, body }` and not an array of strings — the brief calls this out
 * as a real bug caught by reading live data rather than guessing at a template.
 * Written down as a schema, it stays caught.
 */
const termsSchema = z.object({
  heading: shortText,
  note: text,
  items: z.array(z.object({ l: shortText, body: text })).max(40),
})

const contactSchema = z.object({
  address: shortText,
  /** As displayed, e.g. "256.202.6622". */
  phone: shortText,
  /** Digits only, for the `tel:` link. */
  phoneHref: z.string().max(40),
  /** Trimmed: the site builds `mailto:` from it, and a leading space breaks the link. */
  email: shortText.trim(),
  /** Short "City, State" for the utility bar. */
  location: shortText,
  /** Shown only to guests who have already booked. */
  bookedEmail: shortText.trim().optional(),
})

const ctaSchema = z.object({
  heading: shortText,
  body: text,
  image: imageRef,
  btnPrimary: shortText,
  btnSecondary: shortText,
  bookingWedding: pageLink,
  /** Absent means the private-event option shows as coming soon. */
  bookingPrivate: pageLink.optional(),
})

const footerSchema = z.object({ managed: shortText, copyright: shortText })

/**
 * Co-branding: the venue's mark and the agency's, side by side.
 *
 * `cseLogo` was dropped from the first version of this contract, on the brief's
 * word that it had never been set — and three of the ten documents carried it as
 * an empty string, which agreed. The pricing site now wants both marks in its
 * nav and footer, so it is back, and this time something fills it.
 *
 * All three are optional and an empty one means "none": the site falls back to
 * text, which is the documented behavior of every image field here.
 */
const brandSchema = z.object({
  cseUrl: pageLink.optional(),
  /**
   * The venue's own mark, for a **light** background — the nav.
   *
   * A mark usually exists twice: dark artwork for a light background and a
   * white knockout for a dark one. They are the same logo and neither works
   * where the other belongs, so one field cannot serve both — a white mark on a
   * white nav is not a styling flaw, it is an invisible logo.
   */
  logo: imageRef.optional(),
  /** The same mark for a **dark** background — the footer. Absent means use `logo`. */
  logoOnDark: imageRef.optional(),
  /** Common Sense Events' mark. The same image on every venue today. */
  cseLogo: imageRef.optional(),
})

/**
 * The body sections, in the order a page renders them by default.
 *
 * `hero`, `intro` and `guide` always open a page and `footer` always closes it,
 * so none of them is orderable. `contact` and `brand` are not body sections
 * either: one renders with the footer, the other is the marks above.
 *
 * This list is the default *and* the vocabulary — `sectionOrder` may name only
 * these, so a key naming no section is refused at save time rather than
 * silently rendering nothing.
 */
export const VENUE_MARKETING_BODY_SECTIONS = [
  'snapshot',
  'included',
  'spaces',
  'pricing',
  'premium',
  'buyout',
  'addons',
  'barpackages',
  'lodging',
  'overnight',
  'outdoor',
  'packages',
  'spend',
  'micro',
  'grapeband',
  'privateEvents',
  'alacarte',
  'community',
  'cta',
  'terms',
] as const

export type VenueMarketingBodySection = (typeof VENUE_MARKETING_BODY_SECTIONS)[number]

/** One section name, for narrowing a stored value without asserting it. */
export const venueMarketingBodySectionSchema = z.enum(VENUE_MARKETING_BODY_SECTIONS)

/**
 * What order to render the body in, when the default is not what a venue wants.
 *
 * Each entry names a section. A listed section that is absent or empty is
 * skipped by the site, and a section present but *not* listed is appended in
 * default order — so a page can never silently lose content by being reordered,
 * which is the failure mode worth designing against. Absent means "use the
 * default order".
 *
 * **Two schemas, because reading and writing owe opposite duties** — the same
 * split this whole contract is built on, which the first version of this field
 * got wrong by using the strict one for both.
 *
 * Reading is lenient: an entry naming a section *this build* has not heard of is
 * dropped, and duplicates collapse. A strict enum here would mean that renaming
 * or retiring any section turns every page whose stored order still names it
 * into a 503 — the whole document refused over one stale word in a list about
 * presentation. Losing an entry loses an ordering preference; losing the
 * document loses the venue's page.
 *
 * Writing is strict: a name that matches no section is a typo, and the moment to
 * catch a typo is when somebody saves it.
 */
const sectionOrderReadSchema = z
  .array(z.string())
  .max(200)
  .transform((list) => {
    const seen = new Set<string>()
    const kept: VenueMarketingBodySection[] = []
    for (const entry of list) {
      const parsed = venueMarketingBodySectionSchema.safeParse(entry)
      if (parsed.success && !seen.has(parsed.data)) {
        seen.add(parsed.data)
        kept.push(parsed.data)
      }
    }
    return kept
  })

/** The strict half: what a save is held to. See `sectionOrderReadSchema`. */
export const sectionOrderSaveSchema = z
  .array(venueMarketingBodySectionSchema)
  .max(VENUE_MARKETING_BODY_SECTIONS.length)
  .refine(
    (order) => new Set(order).size === order.length,
    'A section can only be listed once in the order.',
  )

/** A full-width banner. Named for the winery template it came from, kept as-is. */
const grapebandSchema = z.object({ heading: shortText, body: text, image: imageRef })

// ── Optional sections. Presence is the on switch; there is no flag. ──────────

const premiumPackageSchema = z.object({
  title: shortText,
  price: dollarPrice,
  timeline: shortText,
  capacity: shortText,
  lodging: text,
})

const premiumSchema = z.object({
  heading: shortText,
  body: text,
  packages: z.array(premiumPackageSchema).max(20),
})

const privateEventsSchema = z.object({
  heading: shortText,
  body: text,
  price: priceText,
  items: z.array(shortText).max(40),
  addons: z.array(z.object({ name: shortText, price: priceText })).max(40),
})

const buyoutSchema = z.object({ heading: shortText, items: z.array(shortText).max(40) })

const communitySchema = z.object({
  heading: shortText,
  body: text,
  linkText: shortText,
  /** Empty renders the text without a link. */
  linkUrl: pageLink,
})

const outdoorSchema = z.object({
  heading: shortText,
  body: text,
  activities: z
    .array(
      z.object({
        title: shortText,
        price: priceText,
        duration: shortText.optional(),
        details: text,
      }),
    )
    .max(20),
})

const barPackagesSchema = z.object({
  heading: shortText,
  body: text,
  /** One long free-text string, not a structured list. The brief is explicit. */
  minimums: text,
  perks: text,
  packages: z
    .array(
      z.object({
        name: shortText,
        price: priceText,
        hours: shortText,
        extra: shortText,
        desc: text,
      }),
    )
    .max(20),
})

const lodgingSchema = z.object({
  heading: shortText,
  body: text,
  weekday: priceText,
  weekend: priceText,
})

const overnightSchema = z.object({
  heading: shortText,
  body: text,
  rates: z.array(z.object({ type: shortText, range: priceText })).max(20),
})

const spacesSchema = z.object({
  heading: shortText,
  body: text,
  list: z.array(z.object({ name: shortText, desc: text })).max(30),
})

const alacarteSchema = z.object({
  heading: shortText,
  body: text,
  options: z.array(z.object({ name: shortText, price: priceText, includes: text })).max(30),
})

/**
 * All-inclusive wedding packages, per person.
 *
 * The pricing site already renders this section (`packages`, between outdoor
 * activities and typical spend). It was missing here, so a stored section was
 * stripped on the way out and refused on the way in — the page never showed it.
 */
const packagesSchema = z.object({
  heading: shortText,
  body: text,
  tiers: z
    .array(z.object({ name: shortText, perperson: priceText, desc: text }))
    .max(20),
})

/**
 * The whole document.
 *
 * `lodging` and `overnight` are alternatives — a venue has a single on-site
 * option with two rates, or several types with ranges, and never both. The brief
 * says "nothing enforces that". This does, because a page rendering two
 * accommodation sections is a page that reads as a mistake.
 */
/**
 * The sections, before anything is said about unknown keys.
 *
 * Shared by the response schema below and the save-time schema in
 * `lib/src/input/venue-marketing.input.ts`, which differ on exactly one point
 * and must not differ on any other.
 */
export const venueMarketingSections = z
  .object({
    hero: heroSchema,
    intro: introSchema,
    guide: guideSchema,
    snapshot: snapshotSchema,
    included: includedSchema,
    pricing: pricingSchema,
    addons: addonsSchema,
    spend: spendSchema,
    micro: microSchema,
    terms: termsSchema,
    contact: contactSchema,
    cta: ctaSchema,
    footer: footerSchema,
    brand: brandSchema,
    grapeband: grapebandSchema,

    premium: premiumSchema.optional(),
    privateEvents: privateEventsSchema.optional(),
    buyout: buyoutSchema.optional(),
    community: communitySchema.optional(),
    outdoor: outdoorSchema.optional(),
    packages: packagesSchema.optional(),
    barpackages: barPackagesSchema.optional(),
    lodging: lodgingSchema.optional(),
    overnight: overnightSchema.optional(),
    spaces: spacesSchema.optional(),
    alacarte: alacarteSchema.optional(),
    /**
     * What order to render the body in. Absent means the default order.
     *
     * Not a section — ordering metadata that happens to live at the top level,
     * which is why it is the one top-level key with no editor of its own.
     */
    sectionOrder: sectionOrderReadSchema.optional(),
  })

/**
 * The three sections whose prices sit beside the site's own dollar sign, as a
 * save holds them: each price a plain number. Reading leaves them as stored
 * (see `dollarPrice`); `saveVenueMarketingContentSchema` swaps these in.
 */
export const dollarPriceSaveSections = {
  snapshot: snapshotSchema.extend({
    cards: z.array(snapshotCardSchema.extend({ price: displayPriceNumber })).max(12),
  }),
  premium: premiumSchema
    .extend({ packages: z.array(premiumPackageSchema.extend({ price: displayPriceNumber })).max(20) })
    .optional(),
  micro: microSchema.extend({
    cards: z.array(microCardSchema.extend({ price: displayPriceNumberOrEmpty.optional() })).max(20),
  }),
}

/** Lodging and overnight describe the same thing two ways; a page shows one. */
export const notBothStayKinds = {
  // Typed by what it reads — whether each key is present — rather than by what
  // the sections contain, which it never looks at.
  check: (content: { lodging?: object | undefined; overnight?: object | undefined }): boolean =>
    !(content.lodging !== undefined && content.overnight !== undefined),
  message:
    'Use lodging or overnight, not both: they are two ways of describing the same thing and ' +
    'the page would show accommodation twice.',
}

/**
 * What the content API serves, and what our own form reads back.
 *
 * **Deliberately not `.strict()`.** A response schema has to survive meeting a
 * newer server: on 2026-08-20 a strict auth DTO rejected every login for tabs
 * running older JavaScript the moment the API added a field, and
 * `response-shapes.test.ts` exists to stop that happening twice. A section added
 * here must be ignorable by a build that predates it, not fatal to it.
 *
 * Saving is the other half of that trade and is strict — see
 * `saveVenueMarketingContentSchema`.
 */
export const venueMarketingContentSchema = venueMarketingSections.refine(
  notBothStayKinds.check,
  { message: notBothStayKinds.message },
)

export type VenueMarketingContent = z.infer<typeof venueMarketingContentSchema>
